import * as _ from 'lodash';
import * as moment from 'moment'
import * as hash from 'object-hash';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IBaseResponse, ICenter, IRecipientMedicalDetails, IRecipientMedicalDetailsRequest } from '@nkr/models/index';
import { MatchStatus } from '@nkr/enums/index';
import { AppActionCreators, IApplicationState, RecipientActionCreators, IRecipientMedicalDetailsResponse } from '@nkr/store/index';
import { AfterAction, RecipientProfileContext, ToggleType } from '../models';
import { IValidation, initialState, medicalDetailsReducer } from './state';
import { ProfileWrapper } from '../styles';
import { toast } from 'react-toastify';
import { ILookupData } from '../lookup-data';
import Content from './content';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';


interface IProps {
    saveRef: MutableRefObject<any>,
    lookupData: ILookupData
}

interface IStateProps {
    center: ICenter,
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getMedicalDetails: (centerId: number, recipientId: number) => Promise<IRecipientMedicalDetailsResponse>,
    postMedicalDetails: (centerId: number, recipientId: number, data: IRecipientMedicalDetailsRequest) => Promise<IBaseResponse>,
}

type TProps = IStateProps & IDispatchProps & IProps;

const MedicalDetails = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(medicalDetailsReducer, initialState);
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    useEffect(() => {
        getData();
    }, []);

    const handleDuplicateError = (error: any): boolean => {
        if (error.response.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    const getData = () => {
        props.showLoading('Retrieving recipient information, one moment please').then(() => {
            props.getMedicalDetails(context.centerId, context.recipientId)
                .then(response => {
                    if (response.data.success) {
                        let data = response.data.results;

                        let dataHash = hash(data);

                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data,
                                hash: dataHash,
                            }
                        });
                    }
                })
                .catch((err: any) => {
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const validate = () => {
        let today = moment();

        let dialysisStartDateValid = true;

        if (state.data.isOnDialysis) {
            if (state.data.dialysisStartDate == '') {
                dialysisStartDateValid = false;
            } else {
                let dialysisStartDate = moment(state.data.dialysisStartDate);

                if (dialysisStartDate.isValid() == false) {
                    dialysisStartDateValid = false;
                } else {
                    if (dialysisStartDate.isSameOrBefore(today, 'day') == false) {
                        dialysisStartDateValid = false;
                    }
                }
            }
        }

        let birthDateValid = true;
        let weightLbsValid = true;
        let weightKgsValid = true;

        let birthDate = moment(state.data.birthDate, 'YYYY-MM-DD');
        let currentYear = moment().year();

        if (birthDate.isValid()) {
            let age = currentYear - birthDate.year();

            if (age < 0 || age > 100) {
                birthDateValid = false;
            }
        } else {
            birthDateValid = false;
        }

        let kgs = state.data.weight;
        let lbs = Math.round(kgs * 2.2);

        if (lbs > 1000) {
            weightLbsValid = false;
        }

        if (kgs > 453.59) {
            weightKgsValid = false;
        }

        let validation: IValidation = {
            dialysisStartDate: dialysisStartDateValid,
            birthDate: birthDateValid,
            weightLbs: weightLbsValid,
            weightKgs: weightKgsValid
        };

        return validation;
    };

    const getBloodType = (bloodType: string) => bloodType ? bloodType.toUpperCase() : 'DNK';

    const normalizeAbo = (histogeneticsAbo: string) => histogeneticsAbo ? histogeneticsAbo.replace('1', '').replace('2', '').toUpperCase() : histogeneticsAbo;


    const loadUSMetrics = () => {
        const { weight, height } = state.data;

        // --- weight ---
        const kgs = weight;
        const lbs = Math.round(kgs * 2.2);

        // --- height ---
        const totalInches = Math.round(height / 2.54); // round once
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;

        const newData = _.cloneDeep(state.data);
        newData.heightInches = totalInches;
        newData.weightLbs = lbs;
        newData.weight = kgs;


        dispatch({
            type: 'loadUSMetrics',
            value: {
                data: newData,
                weightLbs: lbs,
                heightFeet: feet,
                heightInches: inches
            }
        });


        dispatch({
            type: 'convertWeight',
            value: {
                data: newData,
                weightLbs: lbs,
            }
        });

        dispatch({
            type: 'convertHeight',
            value: {
                data: newData,
                heightFeet: feet,
                heightInches: inches
            }
        });
    };


    const getTiterValue = (bloodType: string, val: number) => {
        if (bloodType == 'B' || bloodType == 'O') {
            if (val) {
                return val;
            }
        }

        return 1;
    };

    const handlConvertWeight = (lbs: number) => {
        let kgs = Math.round(lbs / 2.2);

        // if lbs or kgs is NaN, store undefined instead
        const safeLbs = isNaN(lbs) ? undefined : lbs;
        const safeKgs = isNaN(kgs) ? undefined : kgs;

        let newData = _.cloneDeep(state.data);
        newData['weight'] = safeKgs;
        newData['weightLbs'] = safeLbs;

        let newHash = hash(newData);

        dispatch({
            type: 'convertWeight',
            value: {
                data: newData,
                weightLbs: safeLbs,
            }
        });

        context.updateHasChanges(newHash != state.hash);
    };


    const handleConvertHeight = (feet: number, inches: number) => {
        let cm = Math.round(((feet * 12) + inches) * 2.54);

        let newData = _.cloneDeep(state.data);
        newData['height'] = cm;
        newData['heightInches'] = (feet * 12) + inches;

        let newHash = hash(newData);

        dispatch({
            type: 'convertHeight',
            value: {
                data: newData,
                heightFeet: feet,
                heightInches: inches
            }
        });

        context.updateHasChanges(newHash != state.hash);
    };

    const handleSave = (afterAction: AfterAction) => {
        let { birthYear, birthDate, bloodType, antiATiter, antiATiterValue, ...data } = state.data;

        let newBloodType = getBloodType(bloodType)

        let newData: IRecipientMedicalDetailsRequest = {
            ...data,
            birthDate,
            bloodType: newBloodType,
            antiATiter: getTiterValue(newBloodType, antiATiter),
            antiATiterValue: getTiterValue(newBloodType, antiATiterValue)
        }

        let validation = validate();

        dispatch({
            type: 'updateValidation',
            value: validation
        });

        let isValid = validation.dialysisStartDate && validation.birthDate;

        let aboMismatch = state.data.histogeneticsAbo && newBloodType.toUpperCase() != normalizeAbo(state.data.histogeneticsAbo);
        if (aboMismatch) {
            isValid = false;
        }

        let toggleType = ToggleType.NoMessage;
        let messages = [];

        if (!isValid) {
            toggleType = ToggleType.Errors;

            if (validation.dialysisStartDate == false) {
                messages.push('The dialysis start date should be on or before today');
            }

            if (validation.birthDate == false) {
                messages.push('Please specify a valid birth date');
            }

            if (aboMismatch) {
                messages.push('The specified ABO and the ABO determined by the NKR lab do not match.');
            }
        }

        context.updateErrors(toggleType, messages);

        if (isValid) {
            props.showLoading('Saving medical details, one moment please...').then(() => {
                props.postMedicalDetails(context.centerId, context.recipientId, newData)
                    .then(response => {
                        if (response.data.success) {
                            dispatch({
                                type: 'updateHash',
                                value: hash(data)
                            });

                            context.updateHasChanges(false);
                            context.refreshHeader();

                            toast.success('Medical details updated successfully');
                        }
                    })
                    .catch((error: any) => {
                        handleDuplicateError(error);
                    })
                    .finally(() => {
                        props.hideLoading();
                    });
            });
        }
    };

    const handleDataChange = (data: IRecipientMedicalDetails) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        dispatch({
            type: 'updateData',
            value: data
        });

        context.updateHasChanges(newHash != state.hash);

        let aboFieldChanged = state.data.bloodType != data.bloodType || state.data.histogeneticsAbo != data.histogeneticsAbo;
        if (aboFieldChanged) {
            let resolvedBloodType = getBloodType(data.bloodType);
            let stillMismatched = data.histogeneticsAbo && resolvedBloodType != normalizeAbo(data.histogeneticsAbo);
            if (!stillMismatched) {
                context.updateErrors(ToggleType.NoMessage, []);
            }
        }
    };

    const handleToggleMetric = () => {
        let { showMetric } = state;

        loadUSMetrics();

        if (showMetric) {
            dispatch({
                type: 'toggleMetric',
                value: false
            });
        } else {
            dispatch({
                type: 'toggleMetric',
                value: true
            });
        }
    }

    context.registerSave(props.saveRef, handleSave);

    let { data, showMetric } = state;

    if (context.headerInfo == null)
        return null;

    return (
        <ProfileWrapper>
            <Content
                centerId={context.centerId}
                recipientId={context.recipientId}
                lookupData={props.lookupData}
                heightCm={state.heightCm}
                heightInches={state.heightInches}
                heightFeet={state.heightFeet}
                weightKgs={state.weightKgs}
                weightLbs={state.weightLbs}
                showMetric={showMetric}
                data={data}
                validation={state.validation}
                isInMatch={context.headerInfo.statusId > MatchStatus.NeedsMatch}
                onToggleMetric={handleToggleMetric}
                onChange={handleDataChange}
                onConvertHeight={handleConvertHeight}
                onConvertWeight={handlConvertWeight}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </ProfileWrapper>
    );
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        center: appState.app.center,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getMedicalDetails: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getRecipientMedicalDetails(centerId, recipientId))
        },
        postMedicalDetails: (centerId, recipientId, data) => {
            return dispatch(RecipientActionCreators.postRecipientMedicalDetails(centerId, recipientId, data))
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MedicalDetails);